<!-- A widget displaying a list of Decoders below their Store.

	Slots:

		decoder: Empty by default. Could show additional information, like
			the current Channel of each RadioDecoder. Must be scoped to
			access 'scope.decoder'.

		action: Includes a checkbox by default. Can be used to make
			Decoders in a Store selectable, e.g. to add Spots to them.
-->
<template>
  <div>
    <v-list dense>
      <!-- Store -->
      <v-list-tile
        avatar
        class="mt-1 mb-1 store-tile"
      >
        <v-list-tile-avatar>
          <v-icon
            large
            class="msr--text"
          >
            location_on
          </v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-sub-title style="min-height: 20px">
            <small>{{ store.name }}</small>
            <small
              v-if="store.association_membernumber"
              class="d-block"
              :title="$t('storeAssociationMemberNumber')"
            >{{ store.association_membernumber }}</small>
          </v-list-tile-sub-title>
          <v-list-tile-title>
            <span class="grey--text text--darken-3">
              <span style="font-size: 1.25em">
                <strong>{{ store.city }}</strong>
                {{ store.location || '' }}
              </span>
            </span>
            <div v-if="store.address">
              {{ store.address }}
            </div>
          </v-list-tile-title>
        </v-list-tile-content>
        <v-list-tile-action v-if="showSelectAllOption">
          <v-menu offset-y>
            <v-btn
              slot="activator"
              icon
              class="ma-0"
            >
              <v-icon color="grey darken-2">
                more_vert
              </v-icon>
            </v-btn>
            <v-list class="pa-0">
              <v-list-tile @click="selectAllStoreDecoders()">
                <v-list-tile-avatar>
                  <v-icon color="grey">
                    check_box
                  </v-icon>
                </v-list-tile-avatar>
                <v-list-tile-title>
                  {{ $t('selectAllDecodersForStore') }}
                </v-list-tile-title>
              </v-list-tile>
              <v-divider />
              <v-list-tile @click="deselectAllStoreDecoders()">
                <v-list-tile-avatar>
                  <v-icon color="grey">
                    check_box_outline_blank
                  </v-icon>
                </v-list-tile-avatar>
                <v-list-tile-title>
                  {{ $t('deselectAllDecodersForStore') }}
                </v-list-tile-title>
              </v-list-tile>
              <v-divider />
            </v-list>
          </v-menu>
        </v-list-tile-action>
      </v-list-tile>
      <!-- Decoders -->
      <div
        v-for="decoder in decoders"
        :key="decoder.id"
      >
        <v-list-tile
          class="
            inset-left-within-container
            decoder-values decoder-tile
            list-tile-align-start
          "
          @click="toggleDecoderSelection(decoder)"
        >
          <v-list-tile-avatar @click.stop="goToDecoder(decoder)">
            <icon-tv-decoder
              v-if="decoder.ctype === 'tv'"
              :tvdecoder="decoder"
            />
            <v-icon v-if="decoder.ctype === 'radio'">
              radio
            </v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              <v-layout>
                <div
                  :class="{'msr--text': decoderIdentToSelected[decoder.ident]}"
                  @click.stop="goToDecoder(decoder)"
                >
                  {{ decoder.description }}
                </div>
                <!-- Number of target contents the Decoder is compatible with -->
                <div
                  v-if="decoder.num_compatible_target_contents"
                  class="blue--text ml-auto mr-2"
                  :title="getNumCompatibleTargetContentsTooltip(decoder)"
                >
                  {{ getNumCompatibleTargetContentsText(decoder) }}
                </div>
              </v-layout>
            </v-list-tile-title>
          </v-list-tile-content>
          <slot
            name="decoder"
            :decoder="decoder"
          >
            <!-- Empty by default. -->
          </slot>
          <div
            class="decoder-item-group"
            style="align-self: center"
          >
            <div
              class="decoder-item actions"
              data-cy="selectable-store-decoder-checkbox"
            >
              <slot name="actions">
                <v-checkbox
                  :input-value="decoderIdentToSelected[decoder.ident]"
                  hide-details
                  class="msr--text mt-0"
                  color="msr"
                  @click.stop="toggleDecoderSelection(decoder)"
                />
              </slot>
            </div>
          </div>
        </v-list-tile>
      </div>
    </v-list>
    <div
      v-if="!showDivider"
      class="mt-2"
    />
    <v-divider
      v-if="showDivider"
      class="mt-2"
    />
  </div>
</template>

<script>
import IconTvDecoder from '@/components/IconTvDecoder';

export default {
  components: {
    IconTvDecoder,
  },
  props: {
    /* A Store object. */
    store: {
      type: Object,
      required: true,
    },
    /* A list of Radio- or TVDecoder objects. */
    decoders: {
      type: Array,
      required: true,
    },
    showDivider: {
      type: Boolean,
      default: true,
    },
    /* Whether the Decoder(s) should be preselected. */
    preselect: {
      type: Boolean,
      default: false,
    },
    /**
     * Optional indicator of how many target contents are selected.
     *
     * This can help to indicate how many of those a Decoder is
     * compatible to, when the serialized Decoder has a
     * .num_compatible_target_contents attribute on it.
     */
    numTargetContents: {
      type: Number,
      default: null,
    },
    showSelectAllOption: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    decoderIdentToSelected() {
      return this.$store.state.common.decoderIdentToSelected;
    },
  },
  mounted() {
    if (this.preselect) {
      this.preselectDecoders();
    }
  },
  methods: {
    selectAllStoreDecoders() {
      this.$store.commit('SET_DECODERS_SELECTED_BY_IDENT', {
        selected: true,
        decoderIdents: this.decoders.map((d) => d.ident),
      });
    },
    deselectAllStoreDecoders() {
      this.$store.commit('SET_DECODERS_SELECTED_BY_IDENT', {
        selected: false,
        decoderIdents: this.decoders.map((d) => d.ident),
      });
    },
    getNumCompatibleTargetContentsText(decoder) {
      if (!this.numTargetContents) {
        return '';
      }
      const num = decoder.num_compatible_target_contents;
      if (num === this.numTargetContents) {
        return '';
      }
      return `${num} / ${this.numTargetContents}`;
    },
    getNumCompatibleTargetContentsTooltip(decoder) {
      if (!this.numTargetContents) {
        return '';
      }
      const num = decoder.num_compatible_target_contents;
      if (num === this.numTargetContents) {
        return '';
      }
      return this.$t('decoderCompatibleToNumTargetContents')
        .replace('{{ num }}', num)
        .replace('{{ max }}', this.numTargetContents);
    },
    toggleDecoderSelection(decoder) {
      this.$store.commit('TOGGLE_DECODER_SELECTED_BY_IDENT', decoder.ident);
    },
    goToDecoder(decoder) {
      const path = '/player/' + decoder.ctype + '/' + decoder.id;
      this.$router.push(path);
    },
    preselectDecoders(optionalDecoderId) {
      let decoders = this.decoders;

      if (optionalDecoderId !== undefined) {
        decoders = decoders.filter(
          (decoder) => decoder.id == optionalDecoderId
        );
      }

      const decoderIdents = decoders.map((decoder) => decoder.ident);
      const opts = {
        selected: true,
        decoderIdents: decoderIdents,
      };
      this.$store.commit('SET_DECODERS_SELECTED_BY_IDENT', opts);
      this.$emit('preselected');
    },
    /**
     * To be called from parent component via $refs as needed.
     */
    preselectDecoderById(decoderId) {
      this.preselectDecoders(decoderId);
    },
  },
};
</script>

<style>
/* Ensure full width for Decoder v-list-tile. */
.decoder-tile .v-list__tile {
  width: 100%;
}
</style>

<style scoped>
.decoder-values {
  display: flex;
}

.decoder-item {
  margin: 0.1em 0 0.1em 0;
  word-break: break-word;
}
.decoder-item.actions {
  margin-left: 1em;
  justify-content: flex-end;
}

.decoder-item-group {
  display: flex;
  flex-flow: column nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/SelectableStoreDecoders.vue